"use client";
import { useCallback, useState } from "react";
import cx from "classnames";
import { PageContext } from "../../../types/ui";
import { Vehicle } from "../../../types/vehicle";
import ActionButton, { ActionButtonPrefetchedData } from "../../common-components/vdp-buttons/actionButton";
import { getPhoneForVehicle } from "../../../utils/ui";
import { ClientSiteData, VehicleProperty } from "../../../types/site";
import styles from "./price-details.module.scss";
import ClickToCallButton from "../../common-components/click-to-call-button";
import { Translations } from "../../../utils/translations";
import { CustomTooltipType } from "@/ui/common-components/vehicle-card/customTooltip/customTooltip";
import VehiclePropertyView from "@/ui/common-components/vehicle-card/vehicle-property-view";

interface IPriceDetailsClient {
  pageContext: PageContext;
  vehicle: Vehicle;
  siteData: ClientSiteData;
  priceProperties: VehicleProperty[];
  buttons: ActionButtonPrefetchedData[];
  translations: Translations;
  showClickToCall: boolean;
  contentBelowPrices?: string;
  priceDisclaimer?: string;
}

function getPricePropertyComponent(
  property: VehicleProperty,
  siteData: ClientSiteData,
  currentVehicle: Vehicle,
  index: number,
  primary: boolean,
  translations: Translations,
): any {
  return (
    <VehiclePropertyView
      key={index}
      property={property}
      vehicle={currentVehicle}
      siteData={siteData}
      showPrice
      translations={translations}
      className={styles.info}
      titleClassName={styles.infoTitle}
      callToPriceClassName={cx(styles.price, styles.priceSmall)}
      valueClassName={styles.infoValue}
      tooltipType={CustomTooltipType.VDP_PRICE_DETAILS}
    />
  );
}

export default function PriceDetailsClient({
  pageContext,
  vehicle,
  priceProperties,
  siteData,
  buttons,
  translations,
  showClickToCall,
  contentBelowPrices,
  priceDisclaimer,
}: IPriceDetailsClient) {
  const [, setDiscount] = useState<number | undefined>(undefined);
  const onVehicleDataChanged = useCallback(() => {
    if (vehicle.finalPrice) {
      setDiscount(vehicle.price - vehicle.finalPrice);
    }
  }, [vehicle.finalPrice, vehicle.price]);

  return (
    <>
      <div className={styles.list}>
        <ul>
          {priceProperties?.map((item, index) =>
            getPricePropertyComponent(item, siteData, vehicle, index, index === 0, translations),
          )}
        </ul>
        {!!priceDisclaimer && (
          <>
            <div className={styles.priceDisclaimer}>
              <div className={styles.separator} />
              <div className={styles.priceDisclaimerText}>{priceDisclaimer}</div>
            </div>
          </>
        )}
      </div>
      {!!contentBelowPrices && (
        <div className={styles.contentBelowPrices} dangerouslySetInnerHTML={{ __html: contentBelowPrices }} />
      )}
      {buttons.map((item) => (
        <ActionButton
          key={item.actionButtonData.buttonName}
          prfetchedData={item.prefetchedValue}
          className={styles.button}
          actionButton={item.actionButtonData}
          currentVehicle={vehicle}
          vehicleDataChanged={onVehicleDataChanged}
          layoutType="detail"
        />
      ))}
      {showClickToCall && (
        <ClickToCallButton
          phone={getPhoneForVehicle(siteData, vehicle)}
          isSmartphone={pageContext.isSmartphone}
          translations={translations}
        />
      )}
    </>
  );
}
